import { useState, useEffect, useRef } from 'react';

import { useRootSelector } from 'common/features/featuresReducer';
import { FeatureFlagTypes } from 'common/hooks/useFeatureFlags';
import { selectIsEnabled } from 'common/features/store/duck/ffs';
import { selectBuyNowPaymentMethod, buyNowFlow } from 'common/features/store/duck/ui/buy-now-flow';
import { getPaymentTypesForBuyNow, PaymentMethod } from 'common/api/e-comm/payment-types';
import { useDispatch } from 'react-redux';
import { BuyNowModal } from './BuyNowModal';
import { usePaymentOptions } from 'common/hooks/usePaymentOptions';
import useQueryParams from 'hooks/useQueryParams';
import { Product, ProductQueryParam, ProductVariant } from 'common/api/e-comm/models/Product';

type IUseBuyNowHookResult = {
    isBuyNowEnabled: boolean;
    fetching: boolean;
    paymentMethod?: PaymentMethod;
};

const result: IUseBuyNowHookResult = {
    isBuyNowEnabled: false,
    paymentMethod: undefined,
    fetching: false,
};

const useBuyNowPaymentMethod = () => {
    const [fetching, setFetching] = useState<boolean>(true);
    const paymentMethod = useRootSelector(selectBuyNowPaymentMethod);
    const { loadPaymentOptionsData } = usePaymentOptions();
    const dispatch = useDispatch<any>();

    useEffect(() => {
        (async () => {
            try {
                const result = await getPaymentTypesForBuyNow();
                await loadPaymentOptionsData();
                if (result.error) {
                    // error handling
                } else {
                    dispatch(buyNowFlow.update({ paymentMethod: result.data[0] }));
                }
            } catch (e) {
                // unable to load result
            }
            setFetching(false);
        })();
    }, []);

    return {
        paymentMethod,
        fetching,
    };
};

const useBuyNow = () => {
    result.isBuyNowEnabled = useRootSelector((s) =>
        selectIsEnabled(s, FeatureFlagTypes.enable_buy_now_web)
    );
    const { paymentMethod, fetching } = useBuyNowPaymentMethod();
    result.paymentMethod = paymentMethod;
    result.fetching = fetching;
    return result;
};

/* FEC-1702 - Open Buy Now Automatically from query params.
 *
 * This hook will parse the query parameters when the user lands on the PDP.
 *
 * Buy Now Params:
 *    - denom=<number>
 *    - utm_source=extension_web :: no validation needed
 *    - utm_medium=extension     :: no validation needed
 *    - utm_campaign=buy_now     :: no validation needed
 *
 *
 * Remarks:
 *    - `denom` must be validated against the product's `variants`. IFF found
 *      we must check the variant's `isAvailable` && `isVisible` options.
 */
const useBuyNowParseQueryParams = (
    product?: Product
): { priceOverride?: number; selectedVariant?: string; shouldOpenBuyNow: boolean } => {
    const params = useQueryParams();
    const tab = params.get('type');
    const denom = params.get('denom');

    // Controls for configuring the selected input value
    const priceOverride = useRef<number | undefined>();
    const selectedVariant = useRef<string | undefined>();
    const shouldOpenBuyNow = useRef<boolean>(false);

    useEffect(() => {
        if (product) {
            // Step 1. Make sure all the params line up
            if (product && tab === ProductQueryParam.EGiftCard && denom) {
                // Step 2. Parse Denom & Validate against eGiftCard product variants.
                // 3 possible results
                //    - null
                //    - ['50', '00'] when denom is '50.00'
                //    - ['50']       when denom is '50'
                // Note: We'll use the first value of the array as the denom -- we don't care about floating point values
                const matched_denom = denom.match(/\d+/g);

                if (matched_denom?.length && product) {
                    const parsed_denom = parseInt(matched_denom[0]);
                    let valid = false;
                    // Handle 2 cases -- Product With Variant & Product Without Variant
                    if (
                        product.canOverridePrice &&
                        product.minValue &&
                        product.maxValue &&
                        product.unitPrice
                    ) {
                        valid =
                            product.minValue <= parsed_denom &&
                            parsed_denom <= product?.maxValue &&
                            parsed_denom % product.unitPrice === 0; // must be divisible by the unit price!
                    } else if (product.options.length) {
                        valid = Boolean(
                            product.variants.find(
                                (variant: ProductVariant) =>
                                    variant.price === parsed_denom &&
                                    variant.isAvailable &&
                                    variant.isVisible
                            )
                        );
                    }

                    // Denomination passed validation & was found in an active variant
                    if (valid) {
                        priceOverride.current = product.canOverridePrice ? parsed_denom : undefined;
                        selectedVariant.current = !product.canOverridePrice
                            ? `$${parsed_denom}.00`
                            : undefined;
                        shouldOpenBuyNow.current = true;
                    }
                }
            }
        }
    }, [product]);

    return {
        priceOverride: priceOverride.current,
        selectedVariant: selectedVariant.current,
        shouldOpenBuyNow: shouldOpenBuyNow.current,
    };
};

export { useBuyNow, BuyNowModal, useBuyNowParseQueryParams };
